import { AnnotationContext, NonCancelableCustomEvent, TutorialPanelProps } from '@amzn/awsui-components-react';
import React, { FC } from 'react';

import { Tour } from '../components/tutorials/GuidedTour';
import { toursOnEachPage } from '../components/tutorials/GuidedTourDefinitions';
import GuidedTourStore from '../components/tutorials/GuidedTourStore';
import { OverlayI18nStrings } from '../components/tutorials/i18n';
import TourInvisibleButtonContainer, { getInvisButtonId, TourButtonStages } from '../components/tutorials/RUMEventDispatcherButtons';

/**
 * A note on RUM metrics for guided tours:
 * 
 * RUM metrics are gathered by clicking fake buttons, so that a later analysis can be run on the corresponding 
 * recorded dom-events. If we want to remove this feature, we must remove exactly 4 lines of code from this 
 * file. 3 lines that mark the proper click, and one line that puts the invisible buttons in the app. For future
 * removal, these lines are maked with the following comment "//RUM-METRICS-REMOVAL-MARK". Additionally, this will 
 * create some dead code (the clickButtonWithId function, the unique names of tours, and the InvisibleButtonContainer.tsx)
 * file. All of these can be safely removed following the removal of all lines marked with the comment above. 
 */

export interface GuidedTourContextType {
    // Guided tours should automatically recognize any hotspot as it is rendered or deleted. But sometimes based on how react
    // loads objects, a hotspot will load without triggering the logic that automatically advances a tour to the nearest
    // available hotspot. This will manifest as the page changing and the new closest rendered hotspot not being open. 
    // If you experience this issue, call this method to trigger a reload of the current tour logic. 
    setContentChanged: () => void 
    tourStore: GuidedTourStore
};

const GuidedTourContext = React.createContext<GuidedTourContextType>({setContentChanged:() => {}, tourStore: new GuidedTourStore({})});

export const GuidedTourContextProvider: FC = ({ children }) => {
    const [currentTour, setCurrentTour] = React.useState<Tour | null>(null);
    // this dummy varriable is just toggled betwen true and false arbitrarily to 
    // trigger a rerender. It stores no meaningful information.
    const [dummy, setDummy] =React.useState(false);

    const onStartTour = (event: NonCancelableCustomEvent<TutorialPanelProps.TutorialDetail>) => {
        const tour = event.detail.tutorial as Tour;

        tour.completed = false;
        setCurrentTour(tour);

        clickButtonWithId(getInvisButtonId(tour.name, TourButtonStages.START)); //RUM-METRICS-REMOVAL-MARK
    };

    const onExitTour = () => {
        if (!currentTour) {
            return;
        }

        clickButtonWithId(getInvisButtonId(currentTour.name, TourButtonStages.EXIT)); //RUM-METRICS-REMOVAL-MARK
        setCurrentTour(null);
    };

    const onFinishTour = () => {
        if (!currentTour) {
            return;
        }
        
        currentTour.completed = true;
        clickButtonWithId(getInvisButtonId(currentTour.name, TourButtonStages.FINISH)); //RUM-METRICS-REMOVAL-MARK
        setCurrentTour(null);
    };

    const onStepChange = () => {};
    
    const contentChanged = () => {
        setDummy(!dummy);
    };

    
    let tourStore = null;
    
    try {
        tourStore = new GuidedTourStore(toursOnEachPage);
    } catch (error) {
        tourStore = new GuidedTourStore({});
        console.error('Duplicate tours detected');
    }

    return (
        <GuidedTourContext.Provider value={{setContentChanged: contentChanged, tourStore: tourStore}}>
            <AnnotationContext
                key={'key'}
                currentTutorial={currentTour}
                onStepChange={onStepChange}
                onStartTutorial={onStartTour}
                onExitTutorial={onExitTour}
                onFinish={onFinishTour}
                i18nStrings={OverlayI18nStrings}
            >
                {/**RUM-METRICS-REMOVAL-MARK (on line below}*/}
                <TourInvisibleButtonContainer tourNames={tourStore.getAllTourNames()} /> 
                {children}
            </ AnnotationContext>
        </GuidedTourContext.Provider>
    );
};

function clickButtonWithId(id: string) {
    const element = document.getElementById(id);
    element?.click();
}

export default GuidedTourContext;
